import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://ijbc-backend-1089361061119.us-west1.run.app';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
  padding-top: 100px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  color: #333;
  margin-bottom: 30px;
  text-align: center;
`;

const Form = styled.form`
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 40px;
`;

const InputGroup = styled.div`
  margin-bottom: 16px;

  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #444;
  }

  input, textarea {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 15px;
    font-family: 'Noto Sans KR', sans-serif;
    box-sizing: border-box;
  }

  textarea {
    min-height: 200px;
    resize: vertical;
  }
`;

const Button = styled.button<{ $variant?: 'danger' | 'secondary' }>`
  background: ${({ $variant }) =>
    $variant === 'danger' ? '#ff4d4f' :
    $variant === 'secondary' ? '#6c757d' : '#2c5aa0'};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 15px;

  &:hover {
    opacity: 0.88;
  }

  &:disabled {
    background: #ccc;
    cursor: default;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Item = styled.div`
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.07);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const ItemInfo = styled.div`
  flex: 1;

  h3 {
    margin: 0 0 4px 0;
    font-size: 16px;
    color: #333;
  }

  p {
    margin: 0;
    color: #888;
    font-size: 13px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const UploadBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

const UploadLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0f4ff;
  border: 1px dashed #2c5aa0;
  color: #2c5aa0;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s;

  &:hover {
    background: #e0eaff;
  }
`;

const UploadStatus = styled.span<{ $error?: boolean }>`
  font-size: 13px;
  color: ${({ $error }) => ($error ? '#e53935' : '#2e7d32')};
`;

const PreviewGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const PreviewImg = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #ddd;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.75;
  }
`;

interface AnnouncementData {
  id: string;
  title: string;
  date: string;
  content: string;
  author: string;
}

const defaultForm = () => ({
  title: '',
  date: new Date().toISOString().slice(0, 10),
  content: '',
  author: '관리자',
});

const AdminNews: React.FC = () => {
  const [items, setItems] = useState<AnnouncementData[]>([]);
  const [form, setForm] = useState(defaultForm());
  const [editId, setEditId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [uploadError, setUploadError] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();

  const getApiKey = () => {
    const token = localStorage.getItem('adminToken') ||
      process.env.REACT_APP_ADMIN_API_KEY ||
      'ijbc-admin-secret-key-2025';
    if (!token) { navigate('/admin'); return ''; }
    return token;
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const apiKey = getApiKey();
    if (!apiKey) return;

    setUploading(true);
    setUploadMsg('');
    setUploadError(false);

    try {
      const newUrls: string[] = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_URL}/api/upload-image`, {
          method: 'POST',
          headers: { 'x-api-key': apiKey },
          body: fd,
        });
        const data = await res.json();
        if (data.success && data.fileUrl) {
          newUrls.push(data.fileUrl);
        } else {
          throw new Error(data.error || 'Upload failed');
        }
      }

      // Insert URLs into content at cursor position
      const textarea = textareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const insertion = '\n' + newUrls.join('\n') + '\n';
        const newContent = form.content.slice(0, start) + insertion + form.content.slice(end);
        setForm(f => ({ ...f, content: newContent }));
      } else {
        setForm(f => ({ ...f, content: f.content + '\n' + newUrls.join('\n') + '\n' }));
      }

      setUploadedUrls(prev => [...prev, ...newUrls]);
      setUploadMsg(`${newUrls.length}개 이미지 업로드 완료`);
    } catch (err: any) {
      setUploadError(true);
      setUploadMsg(err.message || '업로드 실패');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const fetchItems = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/announcements`);
      const data = await res.json();
      if (data.success) setItems(data.announcements);
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const apiKey = getApiKey();
    if (!apiKey) return;
    setLoading(true);
    try {
      const url = editId
        ? `${API_URL}/api/announcements/${editId}`
        : `${API_URL}/api/announcements`;
      const res = await fetch(url, {
        method: editId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setForm(defaultForm());
        setEditId(null);
        setUploadedUrls([]);
        setUploadMsg('');
        fetchItems();
      } else {
        alert(data.error || '저장 실패');
      }
    } catch (err) {
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('이 공지사항을 삭제하시겠습니까?')) return;
    const apiKey = getApiKey();
    if (!apiKey) return;
    try {
      const res = await fetch(`${API_URL}/api/announcements/${id}`, {
        method: 'DELETE',
        headers: { 'x-api-key': apiKey },
      });
      const data = await res.json();
      if (data.success) fetchItems();
      else alert(data.error || '삭제 실패');
    } catch (err) {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const handleEdit = (item: AnnouncementData) => {
    setForm({ title: item.title, date: item.date, content: item.content, author: item.author });
    setEditId(item.id);
    window.scrollTo(0, 0);
  };

  return (
    <Container>
      <PageTitle>공지사항 관리</PageTitle>

      <Form onSubmit={handleSubmit}>
        <h3 style={{ marginBottom: '20px', color: '#2c5aa0' }}>
          {editId ? '공지사항 수정' : '새 공지사항 작성'}
        </h3>

        <InputGroup>
          <label>제목</label>
          <input
            type="text"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="공지사항 제목"
            required
          />
        </InputGroup>

        <InputGroup>
          <label>날짜</label>
          <input
            type="date"
            value={form.date}
            onChange={e => setForm({ ...form, date: e.target.value })}
            required
          />
        </InputGroup>

        <InputGroup>
          <label>내용</label>
          <UploadBar>
            <UploadLabel>
              {uploading ? '업로드 중...' : '📷 사진 추가'}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleImageUpload}
                disabled={uploading}
              />
            </UploadLabel>
            {uploadMsg && <UploadStatus $error={uploadError}>{uploadMsg}</UploadStatus>}
          </UploadBar>
          {uploadedUrls.length > 0 && (
            <PreviewGrid>
              {uploadedUrls.map((url, i) => (
                <PreviewImg
                  key={i}
                  src={url}
                  alt=""
                  title="클릭하면 URL 복사"
                  onClick={() => navigator.clipboard.writeText(url)}
                />
              ))}
            </PreviewGrid>
          )}
          <textarea
            ref={textareaRef}
            value={form.content}
            onChange={e => setForm({ ...form, content: e.target.value })}
            placeholder="공지사항 내용을 입력하세요. 사진 추가 버튼으로 이미지를 업로드하면 자동으로 삽입됩니다."
            required
          />
        </InputGroup>

        <InputGroup>
          <label>작성자</label>
          <input
            type="text"
            value={form.author}
            onChange={e => setForm({ ...form, author: e.target.value })}
          />
        </InputGroup>

        <div style={{ display: 'flex', gap: '10px' }}>
          <Button type="submit" disabled={loading}>
            {loading ? '저장 중...' : editId ? '수정 저장' : '공지 등록'}
          </Button>
          {editId && (
            <Button type="button" $variant="secondary" onClick={() => { setForm(defaultForm()); setEditId(null); setUploadedUrls([]); setUploadMsg(''); }}>
              취소
            </Button>
          )}
        </div>
      </Form>

      <h2 style={{ marginBottom: '16px', color: '#333' }}>공지사항 목록 ({items.length}건)</h2>
      <List>
        {items.map(item => (
          <Item key={item.id}>
            <ItemInfo>
              <h3>{item.title}</h3>
              <p>{item.date} · {item.author}</p>
            </ItemInfo>
            <Actions>
              <Button onClick={() => handleEdit(item)}>수정</Button>
              <Button $variant="danger" onClick={() => handleDelete(item.id)}>삭제</Button>
            </Actions>
          </Item>
        ))}
      </List>
    </Container>
  );
};

export default AdminNews;
